import fashionableCanesImage from "@/assets/case-studies/fashionablecanes.webp";
import selfCareImage from "@/assets/case-studies/selfcare.webp";
import type { Locale } from "@/lib/i18n";

/**
 * Central case-study registry.
 *
 * Single source of truth for proof/evidence used by:
 *  - src/components/site/WorkContent.tsx (deep-dive cards)
 *  - src/components/site/SiteHeader.tsx  (Work mega menu + featured panel)
 *  - src/components/blog/BlogIndex.tsx   (Insights "Featured work" card)
 *
 * Hrefs are intentionally frozen: they map to existing published resource
 * articles and must not change.
 */

export type CaseStudyGroup = "migration" | "growth";

export type CaseStudyLocaleCopy = {
  /** Engagement tag, e.g. "Magento → Shopify Plus" */
  tag: string;
  /** Short proof headline */
  headline: string;
  /** Verified evidence line */
  evidence: string;
  /** Existing published article path for this locale */
  href: string;
};

export type CaseStudy = {
  id: string;
  client: string;
  group: CaseStudyGroup;
  /** Lower number = higher priority in featured slots. */
  featuredPriority: number;
  /** Optional local asset. Rendered with object-contain in framed surfaces. */
  image?: string;
  imageAlt?: string;
  en: CaseStudyLocaleCopy;
  es: CaseStudyLocaleCopy;
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: "fashionable-canes",
    client: "Fashionable Canes",
    group: "growth",
    featuredPriority: 1,
    image: fashionableCanesImage,
    imageAlt: "Fashionable Canes Shopify storefront",
    en: {
      tag: "Theme migration · Technical SEO",
      headline: "15,000+ SKUs without sacrificing the search channel",
      evidence:
        "Rankings recovered within 60 days · +180% organic traffic · +350% ROI lift · #1 category SERPs",
      href: "/resources/fashionable-canes-seo-case-study",
    },
    es: {
      tag: "Migración de theme · SEO técnico",
      headline: "15.000+ SKUs sin sacrificar el canal orgánico",
      evidence:
        "Rankings recuperados en 60 días · +180% de tráfico orgánico · +350% de ROI · #1 en categorías",
      href: "/es/recursos/caso-fashionable-canes-seo",
    },
  },
  {
    id: "petwell",
    client: "Petwell",
    group: "migration",
    featuredPriority: 3,
    en: {
      tag: "Magento → Shopify Plus",
      headline: "10,000+ SKUs moved in eight weeks with zero downtime",
      evidence:
        "10,000+ SKUs · 8 weeks · zero downtime · customer/order history and redirect mapping",
      href: "/resources/petwell-magento-migration-case-study",
    },
    es: {
      tag: "Magento → Shopify Plus",
      headline: "10.000+ SKUs migrados en ocho semanas con cero downtime",
      evidence:
        "10.000+ SKUs · 8 semanas · cero downtime · histórico de clientes/pedidos y redirect mapping",
      href: "/es/recursos/caso-petwell-migracion-magento",
    },
  },
  {
    id: "self-care-is-for-everyone",
    client: "Self-Care Is For Everyone",
    group: "growth",
    featuredPriority: 4,
    image: selfCareImage,
    imageAlt: "Self-Care Is For Everyone Shopify storefront",
    en: {
      tag: "Custom theme · Brand growth",
      headline: "A Shopify storefront built to absorb high-visibility launches",
      evidence: "+42% collaboration-launch CVR · -46% mobile LCP · 100% custom theme",
      href: "/resources/self-care-is-for-everyone-growth-case-study",
    },
    es: {
      tag: "Theme custom · Crecimiento de marca",
      headline: "Un storefront Shopify preparado para lanzamientos de alta visibilidad",
      evidence: "+42% de CVR en lanzamiento · -46% de LCP móvil · theme 100% custom",
      href: "/es/recursos/caso-self-care-is-for-everyone-crecimiento",
    },
  },
  {
    id: "mp-health",
    client: "MP Health",
    group: "migration",
    featuredPriority: 5,
    en: {
      tag: "Custom systems · Shopify POS",
      headline: "Commerce logic built around how a clinic actually consumes inventory",
      evidence: "Squarespace → Shopify · 4–6 weeks · multi-component IV inventory · Shopify POS",
      href: "/resources/mp-health-squarespace-migration-case-study",
    },
    es: {
      tag: "Sistemas custom · Shopify POS",
      headline: "Commerce adaptado a cómo una clínica consume inventario realmente",
      evidence: "Squarespace → Shopify · 4–6 semanas · inventario IV por componentes · Shopify POS",
      href: "/es/recursos/caso-mp-health-migracion-squarespace",
    },
  },
  {
    id: "fashionable-inc",
    client: "Fashionable INC",
    group: "growth",
    featuredPriority: 6,
    en: {
      tag: "Shopify Plus · Multi-store partnership",
      headline: "Six-plus years of technical continuity across three Plus stores",
      evidence:
        "6+ years · 3 Shopify Plus stores · theme migrations · SEO · custom development · performance",
      href: "/resources/fashionable-inc-partnership-case-study",
    },
    es: {
      tag: "Shopify Plus · Partnership multi-store",
      headline: "Más de seis años de continuidad técnica en tres tiendas Plus",
      evidence:
        "6+ años · 3 tiendas Shopify Plus · theme migrations · SEO · custom development · performance",
      href: "/es/recursos/caso-fashionable-inc-colaboracion",
    },
  },
  {
    id: "llantas-cavazos",
    client: "Llantas Cavazos",
    group: "migration",
    featuredPriority: 2,
    en: {
      tag: "Complex catalog migration",
      headline: "8,000+ tire SKUs moved to Shopify without disrupting the operation",
      evidence:
        "8,000+ active SKUs · under 7.5 weeks · >90% organic traffic retained in week one · 84 size collections",
      href: "/resources/llantas-cavazos-migration-case-study",
    },
    es: {
      tag: "Migración de catálogo complejo",
      headline: "8.000+ SKUs de llantas migrados a Shopify sin frenar la operación",
      evidence:
        "8.000+ SKUs activos · menos de 7,5 semanas · >90% de tráfico orgánico retenido en semana uno · 84 colecciones",
      href: "/es/recursos/caso-llantas-cavazos-migracion",
    },
  },
];

/** Order used by /work and /es/proyectos deep-dive cards (registry order). */
export function caseStudiesForLocale(locale: Locale) {
  return CASE_STUDIES.map((study) => ({
    id: study.id,
    client: study.client,
    group: study.group,
    image: study.image,
    imageAlt: study.imageAlt,
    ...study[locale],
  }));
}

export function caseStudiesByGroup(group: CaseStudyGroup) {
  return CASE_STUDIES.filter((study) => study.group === group);
}

/**
 * Highest-priority case study. Pass `requireImage` when the consuming surface
 * needs a real local asset (e.g. the Insights featured-work card).
 */
export function featuredCaseStudy(options?: { requireImage?: boolean }): CaseStudy {
  const pool = options?.requireImage ? CASE_STUDIES.filter((s) => s.image) : CASE_STUDIES;
  const sorted = [...(pool.length > 0 ? pool : CASE_STUDIES)].sort(
    (a, b) => a.featuredPriority - b.featuredPriority,
  );
  return sorted[0]!;
}

export const CASE_STUDY_GROUP_LABELS: Record<CaseStudyGroup, { en: string; es: string }> = {
  migration: { en: "Migration & replatform", es: "Migración y replatform" },
  growth: { en: "Growth & engineering", es: "Growth e ingeniería" },
};
